<div class="dialog-host">
  <ng-container *ngIf="isLoading">
    <div class="loading-state" data-testid="wizard-loading">
      <h2 mat-dialog-title id="sasa-archive-title">Generate Shoot-Asset Archive</h2>
      <mat-dialog-content class="loading-content">
        <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
        <span class="loading-text">Preparing archive scope…</span>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button
          type="button"
          mat-stroked-button
          data-testid="wizard-loading-cancel"
          (click)="onCancel()"
        >Cancel</button>
      </mat-dialog-actions>
    </div>
  </ng-container>

  <ng-container *ngIf="!isLoading && errorMessage && !preview">
    <div class="error-only-state" data-testid="wizard-error-only">
      <h2 mat-dialog-title id="sasa-archive-title">Generate Shoot-Asset Archive</h2>
      <mat-dialog-content>
        <div class="error-banner">{{ errorMessage }}</div>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button
          type="button"
          mat-stroked-button
          data-testid="wizard-error-close"
          (click)="onCancel()"
        >Close</button>
        <button
          type="button"
          mat-flat-button
          color="primary"
          data-testid="wizard-error-retry"
          (click)="loadPreview()"
        >Retry</button>
      </mat-dialog-actions>
    </div>
  </ng-container>

  <ng-container *ngIf="!isLoading && preview">
    <h2 mat-dialog-title id="sasa-archive-title">Generate Shoot-Asset Archive</h2>

    <div class="submit-error-strip" *ngIf="errorMessage" data-testid="wizard-submit-error">
      {{ errorMessage }}
    </div>

    <mat-dialog-content>
      <div
        class="validation-summary"
        [class.error]="!allHaveAssets"
      >
        <mat-icon
          class="status-icon"
          [class.status-green]="allHaveAssets"
          [class.status-red]="!allHaveAssets"
        >
          {{ allHaveAssets ? 'check_circle' : 'error' }}
        </mat-icon>
        <span
          class="summary-text"
          [class.status-green]="allHaveAssets"
          [class.status-red]="!allHaveAssets"
        >
          {{ productsWithAssetsCount }}/{{ totalProductsCount }} products have shoot assets
        </span>
        <span class="spacer"></span>
        <button
          type="button"
          mat-stroked-button
          class="expand-all-btn"
          data-testid="expand-all"
          (click)="toggleAll()"
        >
          {{ anyExpanded ? 'Collapse All' : 'Expand All' }}
        </button>
      </div>

      <div class="media-filter-bar">
        <span class="label">Media types:</span>
        <mat-checkbox
          data-testid="filter-still"
          color="primary"
          [checked]="selectedMediaTypes.has('Still')"
          (change)="toggleMediaType('Still')"
        >
          Include Stills ({{ totalStillsCount }})
        </mat-checkbox>
        <mat-checkbox
          data-testid="filter-video"
          color="primary"
          [checked]="selectedMediaTypes.has('Video')"
          (change)="toggleMediaType('Video')"
        >
          Include Videos ({{ totalVideosCount }})
        </mat-checkbox>
      </div>

      <div
        *ngIf="selectedMediaTypes.size === 0"
        class="filter-hint"
        data-testid="filter-hint"
      >
        Select at least one media type to build an archive.
      </div>

      <div class="product-list">
        <div
          class="product-row"
          *ngFor="let row of rows; trackBy: trackByProductId"
        >
          <div
            class="product-header"
            [class.clickable]="!row.isBlocked"
            [class.blocked]="row.isBlocked"
            (click)="toggleRow(row)"
          >
            <mat-icon
              class="expand-icon"
              [class.placeholder-icon]="row.isBlocked"
            >
              {{
                row.isBlocked
                  ? 'remove'
                  : isProductExpanded(row.data.product.id)
                    ? 'expand_more'
                    : 'chevron_right'
              }}
            </mat-icon>

            <mat-icon
              class="status-icon"
              [class.status-green]="!row.isBlocked"
              [class.status-red]="row.isBlocked"
            >
              {{ row.isBlocked ? 'warning' : 'check_circle' }}
            </mat-icon>

            <span
              class="product-name"
              [class.status-green]="!row.isBlocked"
              [class.status-red]="row.isBlocked"
            >
              {{ row.data.product.barcode || row.data.product.id }}
            </span>

            <span class="spacer"></span>

            <span *ngIf="row.isBlocked" class="no-label-text">
              No Enhancement Variant assigned
            </span>

            <span *ngIf="!row.isBlocked" class="generated-at-text">
              <ng-container *ngIf="row.stillsCount > 0">
                {{ row.stillsCount }} {{ row.stillsCount === 1 ? 'still' : 'stills' }}
              </ng-container>
              <ng-container *ngIf="row.stillsCount > 0 && row.videosCount > 0"> · </ng-container>
              <ng-container *ngIf="row.videosCount > 0">
                {{ row.videosCount }} {{ row.videosCount === 1 ? 'video' : 'videos' }}
              </ng-container>
              <ng-container *ngIf="row.stillsCount === 0 && row.videosCount === 0">
                0 assets
              </ng-container>
            </span>

            <app-product-identifiers-popover
              [product]="row.popoverProduct"
              [extraRows]="row.extraRows"
              (click)="$event.stopPropagation()"
            ></app-product-identifiers-popover>
          </div>

          <div
            *ngIf="!row.isBlocked && isProductExpanded(row.data.product.id)"
            class="product-preview-body"
          >
            <div
              *ngIf="row.filteredTiles.length === 0"
              class="preview-empty"
            >
              No matching assets for the current filter.
            </div>
            <div class="tile-grid" *ngIf="row.filteredTiles.length > 0">
              <div
                class="tile"
                [class.video]="tile.mediaType === 'Video'"
                *ngFor="let tile of row.filteredTiles; trackBy: trackByTileId"
                [style.backgroundImage]="'url(\'' + tile.thumbnailUri + '\')'"
              >
                <span class="msta">MSTA {{ tile.mstaCode }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button
        type="button"
        mat-stroked-button
        data-testid="wizard-cancel"
        (click)="onCancel()"
      >Cancel</button>
      <button
        type="button"
        mat-flat-button
        color="primary"
        data-testid="wizard-create"
        [disabled]="isCreateDisabled"
        (click)="onCreate()"
      >
        <mat-progress-spinner
          *ngIf="isSubmitting"
          data-testid="wizard-create-spinner"
          mode="indeterminate"
          diameter="14"
        ></mat-progress-spinner>
        <span>{{ isSubmitting ? 'Creating…' : 'Create Archive' }}</span>
      </button>
    </mat-dialog-actions>
  </ng-container>
</div>
